<template>
  <div>
    <BobProPage
      ref="bobProPageRef"
      :pageConfig="pageConfig"
      :columns="tableColumns"
      :tableConfig="tableConfig"
      @refresh="onRefresh"
    />

    <SignatureRecordDetailDrawer
      v-if="detailDrawer.visible"
      :recordId="detailDrawer.recordId"
      @close="onCloseDetail"
      @refresh="onRefresh"
    />

    <ExportOptionsDialog
      v-if="exportDialog.visible"
      :title="t('member.signatureRecord.page.export')"
      allRange
      useExportTask
      @close="exportDialog.visible = false"
      @export="onConfirmExport"
    >
      <template #before-export-range>
        {{ t('member.signatureRecord.page.exportNote') }}
      </template>
    </ExportOptionsDialog>

    <AddSignatureDialog
      v-if="showAddDialog"
      :memberId="memberId"
      @close="showAddDialog = false"
      @refresh="onAddSuccess"
    />
  </div>
</template>

<script>
import { computed, defineComponent, reactive, ref } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import { get } from 'lodash'
import BobProPage from '@/components/BobProPage.vue'
import ExportOptionsDialog from '@/components/Dialog/ExportOptionsDialog.vue'
import SearchTextInput from '@/components/Input/SearchTextInput.vue'
import { GetSignatureRecordMapping, GetSignatureRecordMappingCount } from '@/api/signatureRecordMapping'
import { CreateSignatureRecordMappingExportTask } from '@/api/exportTask'
import { signatureRecordStatusConfig, signatureRecordStatusOptions, signatureSourceTypeConfig } from '@/config/signatureRecordMapping'
import { usePermissions } from '@/use/permissions'
import { useSignatureRecordUtils } from '@/use/useSignatureRecord'
import { useBobProPage } from '@/use/useBobProPage'
import { useExportCenter } from '@/use/useExportCenter'
import { useShop } from '@/use/shop'
import { formatDate } from '@/utils/date'
import AddSignatureDialog from '@/views/Members/components/AddSignatureDialog.vue'
import SignatureRecordDetailDrawer from './components/SignatureRecordDetailDrawer.vue'

export default defineComponent({
  name: 'SignatureDistributionRecords',
  components: {
    BobProPage,
    SignatureRecordDetailDrawer,
    ExportOptionsDialog,
    AddSignatureDialog,
  },
  props: {
    // 預設會員 ID（用於會員詳情頁面）
    memberId: {
      type: String,
      default: undefined,
    },
    // 隱藏頁面標題（用於 tab 內嵌場景）
    hideTitle: {
      type: Boolean,
      default: false,
    },
    // 隱藏的 filters（陣列形式）
    // 可用值: 'templateName', 'member', 'status'
    hideFilters: {
      type: Array,
      default: () => [],
    },
    // 隱藏的表格欄位（陣列形式）
    // 可用值: 'Member.UserInfo.name', 'Member.UserInfo.phone', 及其他欄位 prop
    hideTableColumns: {
      type: Array,
      default: () => [],
    },
    // 隱藏匯出按鈕
    hideExport: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { bobProPageRef, onRefresh } = useBobProPage()
    const { checkAction } = usePermissions()
    const { shopId } = useShop()
    const { messageOptions } = useExportCenter()
    const { getOrderCode, getCopyLinkStatus, copySignatureLink } = useSignatureRecordUtils()

    const showAddDialog = ref(false)

    const canCreate = computed(() => {
      return checkAction('admin.signatureRecordMapping.create')
    })

    const onOpenAddDialog = () => {
      showAddDialog.value = true
    }

    const onAddSuccess = async () => {
      showAddDialog.value = false
      onRefresh()
    }

    const detailDrawer = reactive({
      visible: false,
      recordId: '',
    })

    const exportDialog = reactive({
      visible: false,
    })

    const onOpenDetail = (row) => {
      detailDrawer.recordId = get(row, 'id')
      detailDrawer.visible = true
    }

    const onCloseDetail = () => {
      detailDrawer.visible = false
      detailDrawer.recordId = ''
    }

    const onExport = async () => {
      const search = get(bobProPageRef.value, 'search', {})
      const payload = {
        shopId: shopId.value,
        templateName: search.templateName || undefined,
        MemberId: search.member?.id || undefined,
        status: search.status || undefined,
      }

      const [, errMsg] = await CreateSignatureRecordMappingExportTask(payload)
      if (errMsg) {
        window.$message.error(errMsg)
        return
      }

      window.$message(messageOptions.value)
    }

    const onOpenExportDialog = () => {
      exportDialog.visible = true
    }

    const onConfirmExport = async () => {
      exportDialog.visible = false
      await onExport()
    }

    const translatedStatusOptions = computed(() =>
      signatureRecordStatusOptions.map(opt => ({ ...opt, label: t(opt.label) })),
    )

    const allFilters = [
      {
        component: SearchTextInput,
        key: 'templateName',
        props: (filters) => ({
          placeholder: t('member.signatureRecord.filter.templateNamePlaceholder'),
          value: filters.templateName,
        }),
        on: (filters) => ({
          'update:value': (value) => {
            filters.templateName = value
          },
        }),
      },
      {
        component: () => import('@/components/Search/MemberSearch.vue'),
        key: 'member',
        props: () => ({
          placeholder: t('member.signatureRecord.filter.memberPlaceholder'),
        }),
      },
      {
        component: () => import('@/components/Select/BaseDataSelect.vue'),
        key: 'status',
        props: (filters) => ({
          labelKey: 'label',
          valueKey: 'value',
          objKey: 'value',
          value: filters.status,
          clearable: true,
          filterable: false,
          placeholder: t('member.signatureRecord.filter.statusPlaceholder'),
          dataList: translatedStatusOptions.value,
        }),
        on: (filters) => ({
          'update:value': (value) => {
            filters.status = value
          },
        }),
      },
    ]

    // 根據 hideFilters 過濾
    const filterConfig = computed(() => {
      return allFilters.filter(f => !props.hideFilters.includes(f.key))
    })

    const pageConfig = computed(() => {
      const actions = []

      // 內嵌模式（有 memberId）：顯示新增同意書按鈕
      if (props.memberId && canCreate.value) {
        actions.push({
          key: 'add',
          component: () => import('@/components/BaseElButton.vue'),
          render: () => t('member.signatureRecord.page.addAgreement'),
          props: { type: 'primary' },
          on: { click: onOpenAddDialog },
        })
      }

      // 獨立頁面模式：顯示匯出按鈕
      if (!props.hideExport) {
        actions.push({
          key: 'export',
          component: 'BaseElButton',
          render: () => t('member.signatureRecord.page.export'),
          props: { type: 'primary' },
          on: { click: onOpenExportDialog },
        })
      }

      return {
        title: props.hideTitle ? '' : t('member.signatureRecord.page.title'),
        titleSize: 24,
        titleHint: !props.hideTitle,
        actions,
      }
    })

    const tableColumns = computed(() => {
      const allColumns = [
        {
          prop: 'Member.UserInfo.name',
          label: t('member.signatureRecord.table.memberName'),
          render: (row) => get(row, 'Member.UserInfo.name', '-'),
        },
        {
          prop: 'Member.UserInfo.phone',
          label: t('member.signatureRecord.table.memberPhone'),
          render: (row) => get(row, 'Member.UserInfo.phone', '-'),
        },
        {
          prop: 'SignatureTemplate.name',
          label: t('member.signatureRecord.table.templateName'),
          render: (row) => get(row, 'SignatureTemplate.name', '-'),
        },
        {
          prop: 'sourceType',
          label: t('member.signatureRecord.table.sourceType'),
          render: (row) => {
            const labelKey = get(signatureSourceTypeConfig, `${get(row, 'sourceType')}.label`)
            return labelKey ? t(labelKey) : '-'
          },
        },
        {
          prop: 'orderCode',
          label: t('member.signatureRecord.table.orderCode'),
          render: (row) => getOrderCode(row),
        },
        {
          prop: 'signDeadline',
          label: t('member.signatureRecord.table.signDeadline'),
          render: (row) => {
            const date = get(row, 'signDeadline')
            return date ? formatDate(date, 'YYYY/MM/DD HH:mm') : '-'
          },
        },
        {
          prop: 'SignatureRecord.effectiveAt',
          label: t('member.signatureRecord.table.signedAt'),
          render: (row) => {
            const date = get(row, 'SignatureRecord.effectiveAt')
            return date ? formatDate(date, 'YYYY/MM/DD HH:mm') : '-'
          },
        },
        {
          prop: 'status',
          label: t('member.signatureRecord.table.status'),
          component: () => import('@/components/Tag/Tag.vue'),
          props: (row) => ({
            type: get(signatureRecordStatusConfig, `${get(row, 'status')}.tagType`, 'info'),
          }),
          render: (row) => {
            const labelKey = get(signatureRecordStatusConfig, `${get(row, 'status')}.label`)
            return labelKey ? t(labelKey) : '-'
          },
        },
        {
          prop: 'operate',
          label: t('member.signatureRecord.table.operate'),
          fixed: 'right',
          width: 210,
          component: () => import('@/components/OperateColumn.vue'),
          props: (row) => {
            const items = [
              {
                key: 'detail',
                component: 'BaseElButton',
                render: () => t('member.signatureRecord.table.detail'),
                props: {
                  type: 'text',
                  class: ['underline'],
                },
                on: { click: () => onOpenDetail(row) },
              },
            ]

            const copyLinkStatus = getCopyLinkStatus(row)
            if (copyLinkStatus.canShow) {
              items.push({
                key: 'copy',
                component: 'BaseElButton',
                render: () => t('member.signatureRecord.table.copyLink'),
                props: {
                  type: 'text',
                  disabled: copyLinkStatus.isDisabled,
                  class: ['underline'],
                },
                on: { click: () => copySignatureLink(get(row, 'id')) },
              })
            }

            return {
              class: 'flex gap-[12px] justify-center',
              items,
            }
          },
        },
      ]

      // 根據 hideTableColumns 過濾欄位
      return allColumns.filter(col => !props.hideTableColumns.includes(col.prop))
    })

    const tableConfig = computed(() => {
      return {
        pagination: {
          enabled: true,
          pageLimit: 10,
          pageSizes: [10, 20, 50],
        },
        apis: {
          get: GetSignatureRecordMapping,
          count: GetSignatureRecordMappingCount,
          getPayload: (filters) => ({
            templateName: filters.templateName || undefined,
            MemberId: props.memberId || filters.member?.id || undefined,
            status: filters.status || undefined,
          }),
        },
        filters: filterConfig.value,
      }
    })

    return {
      t,
      bobProPageRef,
      tableColumns,
      tableConfig,
      pageConfig,
      detailDrawer,
      exportDialog,
      showAddDialog,
      onCloseDetail,
      onRefresh,
      onConfirmExport,
      onAddSuccess,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
